// src/components/RecommendedActionsPanel.jsx
// ============================================================================
// RECOMMENDED ACTIONS PANEL
// ============================================================================
// Displays workflow-assistant recommendations for a project. Each action card
// shows what happened (trigger) and what to do next, with Open / Dismiss buttons.
// Integrates with invoice creation, email drafts, and reminder workflows.
// ============================================================================
import React, { useState } from 'react';
import { Mail, FileText, Download, Bell, AlertTriangle, X, ChevronDown, ChevronRight, CheckCircle, Clock } from 'lucide-react';

// ── Icon resolver ───────────────────────────────────────────────────────────

const ICON_MAP = {
  Mail,
  FileText,
  Download,
  Bell,
  AlertTriangle,
};

function ActionIcon({ iconName, color, size = 14 }) {
  const Icon = ICON_MAP[iconName] || Bell;
  return <Icon style={{ width: size, height: size, color, flexShrink: 0 }} />;
}

// ── Category colors ─────────────────────────────────────────────────────────

const CATEGORY_COLORS = {
  invoice:  { bg: 'var(--brio-gold-light, rgba(159,144,42,0.1))',   border: 'var(--brio-gold, #9F902A)' },
  email:    { bg: 'var(--brio-teal-light, rgba(104,149,129,0.12))', border: 'var(--brio-teal, #689581)' },
  reminder: { bg: 'var(--brio-red-light, rgba(147,73,72,0.1))',     border: 'var(--brio-red, #934948)' },
  review:   { bg: 'var(--brio-orange-light, rgba(219,107,93,0.1))', border: 'var(--brio-orange, #DB6B5D)' },
  admin:    { bg: 'var(--brio-yellow-light, rgba(236,210,136,0.15))', border: 'var(--brio-yellow, #ECD288)' },
};

// ── Action → workflow mapping ───────────────────────────────────────────────

const ACTION_EVENT_TYPE_MAP = {
  draft_project_initiated_email: 'project_initiated',
  draft_stage_initiation_email:  'stage_initiated',
  draft_stage_closure_email:     'stage_closure',
  draft_stage_completion_approval_email: 'stage_completion_approval',
  draft_payment_confirmed_email: 'payment_confirmed',
  draft_planned_date_revised_email: 'planned_date_revised',
  draft_tax_invoice_email:       'invoice_sent',
  draft_timeline_change_email:   'timeline_change',
  draft_payment_reminder_email:  'reminder_overdue',
};

// ── Main component ──────────────────────────────────────────────────────────

export default function RecommendedActionsPanel({
  openActions,
  dismissedActions,
  completedActions,
  actionDefs,
  onDismiss,
  onComplete,
  onReopen,
  onLaunchAction,
  loading,
}) {
  const [showDismissed, setShowDismissed] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);

  if (loading) {
    return (
      <div style={{
        padding: '12px 16px', fontSize: '11px', color: 'var(--brio-grey)',
        fontFamily: 'var(--brio-font-family)',
      }}>
        Loading recommended actions…
      </div>
    );
  }

  const totalOpen = openActions?.length || 0;
  const totalDismissed = dismissedActions?.length || 0;
  const totalCompleted = completedActions?.length || 0;

  if (totalOpen === 0 && totalDismissed === 0 && totalCompleted === 0) {
    return null; // Nothing to show
  }

  return (
    <div style={{
      fontFamily: 'var(--brio-font-family)',
      fontSize: '11px',
      marginTop: 'var(--brio-space-s, 12px)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '8px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Clock style={{ width: 14, height: 14, color: 'var(--brio-gold, #9F902A)' }} />
          <span style={{ fontWeight: 600, fontSize: '12px' }}>
            Recommended Next Actions
          </span>
          {totalOpen > 0 && (
            <span style={{
              background: 'var(--brio-gold, #9F902A)', color: '#fff',
              fontSize: '9px', fontWeight: 600,
              padding: '1px 6px', borderRadius: '8px',
            }}>
              {totalOpen}
            </span>
          )}
        </div>
      </div>

      {/* Open actions */}
      {totalOpen === 0 && (
        <div style={{
          padding: '10px 12px', color: 'var(--brio-grey)',
          fontSize: '10px', textAlign: 'center',
          border: '1px dashed var(--brio-border)', background: 'var(--brio-grey-light)',
        }}>
          No pending actions. All caught up.
        </div>
      )}

      {openActions.map(action => (
        <ActionCard
          key={action.id}
          action={action}
          actionDefs={actionDefs}
          onDismiss={onDismiss}
          onLaunchAction={onLaunchAction}
        />
      ))}

      {/* Dismissed section (collapsible) */}
      {totalDismissed > 0 && (
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={() => setShowDismissed(!showDismissed)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '9px', color: 'var(--brio-grey)',
              fontFamily: 'var(--brio-font-family)', padding: '2px 0',
            }}
          >
            {showDismissed
              ? <ChevronDown style={{ width: 10, height: 10 }} />
              : <ChevronRight style={{ width: 10, height: 10 }} />
            }
            {totalDismissed} dismissed
          </button>
          {showDismissed && dismissedActions.map(action => (
            <DismissedCard key={action.id} action={action} onReopen={onReopen} />
          ))}
        </div>
      )}

      {/* Completed section (collapsible) */}
      {totalCompleted > 0 && (
        <div style={{ marginTop: '4px' }}>
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '4px',
              fontSize: '9px', color: 'var(--brio-grey)',
              fontFamily: 'var(--brio-font-family)', padding: '2px 0',
            }}
          >
            {showCompleted
              ? <ChevronDown style={{ width: 10, height: 10 }} />
              : <ChevronRight style={{ width: 10, height: 10 }} />
            }
            {totalCompleted} completed
          </button>
          {showCompleted && completedActions.map(action => (
            <CompletedCard key={action.id} action={action} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Action Card (open) ──────────────────────────────────────────────────────

function ActionCard({ action, actionDefs, onDismiss, onLaunchAction }) {
  const def = actionDefs?.[action.actionType] || {};
  const catColors = CATEGORY_COLORS[def.category] || CATEGORY_COLORS.email;
  const eventType = ACTION_EVENT_TYPE_MAP[action.actionType];

  const isInvoiceCreate = action.actionType === 'create_proforma_invoice' ||
                          action.actionType === 'create_tax_invoice';
  const isReview = action.actionType === 'review_missing_payment_terms';
  const isRadarRouted = action.metadata?.routing === 'radar';

  // Primary button label
  let primaryLabel = 'Open';
  if (isInvoiceCreate) primaryLabel = 'Create Invoice';
  else if (eventType) primaryLabel = 'Draft Email';
  else if (isReview) primaryLabel = 'Review';

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: '8px',
      padding: '8px 10px', marginBottom: '4px',
      background: catColors.bg,
      border: `1px solid ${catColors.border}`,
      borderLeft: `3px solid ${catColors.border}`,
    }}>
      <ActionIcon iconName={def.icon} color={catColors.border} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: '11px', lineHeight: 1.3 }}>
          {action.title}
        </div>
        {action.description && (
          <div style={{ fontSize: '9px', color: 'var(--brio-grey)', marginTop: '2px', lineHeight: 1.3 }}>
            {action.description}
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
          <button
            onClick={() => onLaunchAction(action)}
            style={{
              padding: '2px 10px', fontSize: '9px', fontWeight: 500,
              background: catColors.border, color: '#fff',
              border: 'none', cursor: 'pointer',
              fontFamily: 'var(--brio-font-family)',
            }}
          >
            {primaryLabel}
          </button>
          <button
            onClick={() => onDismiss(action.id)}
            style={{
              padding: '2px 8px', fontSize: '9px',
              background: 'none', border: '1px solid var(--brio-border)',
              cursor: 'pointer', color: 'var(--brio-grey)',
              fontFamily: 'var(--brio-font-family)',
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
      <button
        onClick={() => onDismiss(action.id)}
        title="Dismiss"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '0', lineHeight: 1, flexShrink: 0,
        }}
      >
        <X style={{ width: 12, height: 12, color: 'var(--brio-grey)' }} />
      </button>
    </div>
  );
}

// ── Dismissed Card ──────────────────────────────────────────────────────────

function DismissedCard({ action, onReopen }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '4px 10px', marginBottom: '2px',
      background: 'var(--brio-grey-light)', opacity: 0.7,
      fontSize: '10px', color: 'var(--brio-grey)',
    }}>
      <X style={{ width: 10, height: 10, flexShrink: 0 }} />
      <span style={{ flex: 1, textDecoration: 'line-through' }}>{action.title}</span>
      <button
        onClick={() => onReopen(action.id)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '9px', color: 'var(--brio-gold)', textDecoration: 'underline',
          fontFamily: 'var(--brio-font-family)',
        }}
      >
        Reopen
      </button>
    </div>
  );
}

// ── Completed Card ──────────────────────────────────────────────────────────

function CompletedCard({ action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '4px 10px', marginBottom: '2px',
      background: 'var(--brio-grey-light)', opacity: 0.7,
      fontSize: '10px', color: 'var(--brio-green, #16a34a)',
    }}>
      <CheckCircle style={{ width: 10, height: 10, flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{action.title}</span>
      <span style={{ fontSize: '8px', color: 'var(--brio-grey)' }}>Done</span>
    </div>
  );
}

// Export for workflow handoff
export { ACTION_EVENT_TYPE_MAP };
